/**
 * diagnostic.ts
 *
 * 2026-05-16: Mac 無し環境で iPhone Safari PWA の遅延を可視化する診断モジュール。
 *
 * 起動方法:
 *   URL に `?diag=1` を付ける (例: /c/greensoft/00450/2HKV-DHCA-83HB?diag=1)
 *   → sessionStorage に保存され、以降のページ遷移でも有効
 *   → 画面右下に半透明パネル表示、各 checkpoint の performance.now() を時系列で出す
 *
 * checkpoint:
 *   - app_start, firebase_init, auth_state_start/end, signOut_start/end,
 *     firestore_getDoc_start/end, route_resolved, first_screen_rendered, etc.
 *
 * 18-70 秒遅延の正体 (signOut hang? Firestore long polling fallback? Firebase Auth init stall?)
 * を秒単位で特定する目的。Mac の Safari Web Inspector の代替。
 *
 * 解除方法:
 *   URL に `?diag=0` で sessionStorage 削除。
 */

interface DiagEntry {
  label: string;
  t: number;        // performance.now() at mark
  dt?: number;      // delta from previous mark (computed at render)
  detail?: string;  // optional context
}

const KEY_ENABLED = 'weborder:diag:enabled';
const KEY_EXPANDED = 'weborder:diag:expanded';
let enabled: boolean | null = null;
const entries: DiagEntry[] = [];
const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
let panelEl: HTMLDivElement | null = null;
let toggleBtn: HTMLButtonElement | null = null;
let expanded: boolean = false;
let renderScheduled = false;

function readEnabled(): boolean {
  if (enabled !== null) return enabled;
  try {
    if (typeof window === 'undefined') {
      enabled = false;
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const diag = params.get('diag');
    if (diag === '1') {
      window.sessionStorage.setItem(KEY_ENABLED, '1');
      enabled = true;
    } else if (diag === '0') {
      window.sessionStorage.removeItem(KEY_ENABLED);
      enabled = false;
    } else {
      enabled = window.sessionStorage.getItem(KEY_ENABLED) === '1';
    }
  } catch {
    enabled = false;
  }
  return enabled;
}

/**
 * checkpoint を記録する。enabled でない時は no-op (production overhead ほぼゼロ)。
 */
export function diagMark(label: string, detail?: string): void {
  if (!readEnabled()) return;
  try {
    const t = typeof performance !== 'undefined' ? performance.now() : 0;
    entries.push({ label, t, detail });
    schedulePanelRender();
  } catch {
    // ignore
  }
}

function schedulePanelRender(): void {
  if (renderScheduled) return;
  renderScheduled = true;
  try {
    setTimeout(() => {
      renderScheduled = false;
      renderPanel();
    }, 50);
  } catch {
    renderScheduled = false;
  }
}

function readExpanded(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(KEY_EXPANDED) === '1';
  } catch {
    return false;
  }
}

function setExpanded(v: boolean): void {
  expanded = v;
  try {
    if (v) window.sessionStorage.setItem(KEY_EXPANDED, '1');
    else window.sessionStorage.removeItem(KEY_EXPANDED);
  } catch {
    /* ignore */
  }
  renderPanel();
}

function ensureToggleBtn(): void {
  if (typeof document === 'undefined') return;
  if (toggleBtn) return;
  toggleBtn = document.createElement('button');
  toggleBtn.id = '__weborder_diag_toggle__';
  toggleBtn.type = 'button';
  toggleBtn.setAttribute('aria-label', 'diagnostic panel toggle');
  toggleBtn.textContent = '📋';
  Object.assign(toggleBtn.style, {
    position: 'fixed',
    top: '4px',
    right: '4px',
    width: '36px',
    height: '36px',
    padding: '0',
    fontSize: '18px',
    lineHeight: '36px',
    textAlign: 'center',
    background: 'rgba(0, 0, 0, 0.55)',
    color: '#0ff',
    border: '1px solid #0ff',
    borderRadius: '50%',
    cursor: 'pointer',
    zIndex: '1000000',
    fontFamily: 'monospace',
    WebkitTapHighlightColor: 'transparent',
  } as Partial<CSSStyleDeclaration>);
  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  });
  document.body.appendChild(toggleBtn);
}

function renderPanel(): void {
  if (typeof document === 'undefined') return;
  if (!readEnabled()) return;

  // 2026-05-18 user fb: panel が PIN/ログアウト/注文ボタンを覆って操作不能 → 折りたたみ式に変更。
  // default = 画面右上 36px の `📋` ボタンのみ表示 (操作領域に干渉せず)。
  // tap で展開、もう一度 tap で折りたたむ。展開状態は sessionStorage で永続。
  ensureToggleBtn();

  if (!expanded) {
    expanded = readExpanded();
  }

  // 折りたたみ中は panel を非表示 (DOM ごと削除)、ボタンだけ残す
  if (!expanded) {
    if (panelEl && panelEl.parentNode) {
      panelEl.parentNode.removeChild(panelEl);
      panelEl = null;
    }
    return;
  }

  if (!panelEl) {
    panelEl = document.createElement('div');
    panelEl.id = '__weborder_diag_panel__';
    Object.assign(panelEl.style, {
      position: 'fixed',
      top: '44px', // ボタン下に展開
      right: '4px',
      maxWidth: 'calc(100vw - 8px)',
      width: 'min(420px, calc(100vw - 8px))',
      maxHeight: '60vh',
      overflowY: 'auto',
      background: 'rgba(0, 0, 0, 0.88)',
      color: '#0ff',
      fontFamily: 'monospace',
      fontSize: '10px',
      lineHeight: '1.35',
      padding: '6px 8px',
      zIndex: '999999',
      border: '1px solid #0ff',
      borderRadius: '6px',
      WebkitOverflowScrolling: 'touch',
      pointerEvents: 'auto',
      whiteSpace: 'pre',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(panelEl);
  }

  let prevT = t0;
  const lines: string[] = ['[?diag=1] WebOrder 診断 (📋 で閉じる、?diag=0 で消去)'];
  entries.forEach((e) => {
    const totalMs = (e.t - t0).toFixed(0);
    const deltaMs = (e.t - prevT).toFixed(0);
    const detail = e.detail ? ` // ${e.detail}` : '';
    lines.push(`${totalMs.padStart(6)}ms (Δ${deltaMs.padStart(5)}ms) ${e.label}${detail}`);
    prevT = e.t;
  });
  panelEl.textContent = lines.join('\n');
}

/**
 * 初期化 (App boot 時に 1 回だけ呼ぶ)。?diag=1 検知 + app_start を記録。
 */
export function diagBoot(): void {
  if (!readEnabled()) return;
  diagMark('app_start');
}

/**
 * 現在の entries を JSON で返す (将来 HTTP endpoint 送信用)。
 */
export function diagDump(): DiagEntry[] {
  return entries.slice();
}
